import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';

// Works for any logged-in staff account — SUPER_ADMIN, ADMIN, POS, KITCHEN.
// Riders authenticate via OTP, not a password, so this screen doesn't
// apply to them (and no rider role ever reaches this route).
const ChangePassword = ({ backTo = '/pos-dashboard' }) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('New password must be different from the current one');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(backTo)} className="p-1" data-testid="cp-back">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#E21836]" />
          Change Password
        </h1>
      </div>

      <div className="max-w-md mx-auto p-4">
        <form onSubmit={handleSubmit} className="bg-white border rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Current password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="cp-current"
                className="w-full border rounded-lg px-3 py-2 pr-10 text-sm"
              />
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">New password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="cp-new"
                className="w-full border rounded-lg px-3 py-2 pr-10 text-sm"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">At least 8 characters</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Confirm new password</label>
            <input
              type={showNew ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              data-testid="cp-confirm"
              className="w-full border rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="cp-submit"
            className="w-full bg-[#E21836] hover:bg-[#c8122d] text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? 'Changing...' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
